import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Search, Download, LogOut, Users, RefreshCw, ChevronLeft, ChevronRight,
  Plus, Trash2, X
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 15;

const ROLES = [
  "project-manager",
  "work-manager",
  "electrical-consultant",
  "installation-calculator",
  "team-member",
  "other",
];

// --- Utility ---

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

// --- Custom Hook: Admin Data ---

function useAdminData(navigate) {
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [userName, setUserName] = useState("");

  const fetchSignups = useCallback(async (searchQuery = "") => {
    setLoading(true);
    try {
      const params = searchQuery ? { search: searchQuery } : {};
      const res = await axios.get(`${BACKEND_URL}/api/admin/signups`, {
        params,
        withCredentials: true,
      });
      setSignups(res.data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchCount = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/signups/count`, { withCredentials: true });
      setTotalCount(res.data.count);
    } catch (_) { /* auth redirect handled by fetchSignups */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        if (!cancelled) setUserName(res.data.name || res.data.email);
      } catch {
        if (!cancelled) navigate("/admin");
        return;
      }
      if (!cancelled) {
        fetchSignups();
        fetchCount();
      }
    };
    init();
    return () => { cancelled = true; };
  }, [navigate, fetchSignups, fetchCount]);

  const addSignup = async (formData) => {
    await axios.post(`${BACKEND_URL}/api/admin/signups`, formData, { withCredentials: true });
  };

  const deleteSignup = async (signupId) => {
    await axios.delete(`${BACKEND_URL}/api/admin/signups/${signupId}`, { withCredentials: true });
  };

  const exportCSV = async () => {
    const res = await axios.get(`${BACKEND_URL}/api/admin/signups/export`, {
      withCredentials: true,
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `pm_flow_signups_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (_) { /* proceed to redirect regardless */ }
    navigate("/admin");
  };

  return { signups, loading, totalCount, userName, fetchSignups, fetchCount, addSignup, deleteSignup, exportCSV, logout };
}

// --- Sub-components ---

function StatsCards({ totalCount, signups }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
      <div className="bg-slate-900/80 border border-slate-800 !rounded-[10px] p-5" data-testid="stat-total-signups">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 !rounded-[10px] bg-amber-400/10 flex items-center justify-center">
            <Users size={18} className="text-amber-400" />
          </div>
          <span className="text-slate-400 text-sm">Total Signups</span>
        </div>
        <p className="text-3xl font-bold text-white">{totalCount}</p>
      </div>
      <div className="bg-slate-900/80 border border-slate-800 !rounded-[10px] p-5" data-testid="stat-newsletter">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 !rounded-[10px] bg-emerald-400/10 flex items-center justify-center">
            <Users size={18} className="text-emerald-400" />
          </div>
          <span className="text-slate-400 text-sm">Newsletter Opted In</span>
        </div>
        <p className="text-3xl font-bold text-white">
          {signups.filter(s => s.newsletter).length}
        </p>
      </div>
      <div className="bg-slate-900/80 border border-slate-800 !rounded-[10px] p-5" data-testid="stat-results">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 !rounded-[10px] bg-blue-400/10 flex items-center justify-center">
            <Search size={18} className="text-blue-400" />
          </div>
          <span className="text-slate-400 text-sm">Showing Results</span>
        </div>
        <p className="text-3xl font-bold text-white">{signups.length}</p>
      </div>
    </div>
  );
}

function SignupRow({ signup, idx, globalIdx, deleteConfirm, onDeleteClick, onConfirmDelete, onCancelDelete }) {
  return (
    <tr className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors" data-testid={`signup-row-${idx}`}>
      <td className="px-4 py-3 text-slate-500">{globalIdx}</td>
      <td className="px-4 py-3 text-white font-medium">{signup.full_name}</td>
      <td className="px-4 py-3 text-slate-300">{signup.email}</td>
      <td className="px-4 py-3 text-slate-400">{signup.company || "-"}</td>
      <td className="px-4 py-3 text-slate-400">{signup.role}</td>
      <td className="px-4 py-3 text-slate-400">{signup.country || "-"}</td>
      <td className="px-4 py-3 text-slate-400">{signup.team_size || "-"}</td>
      <td className="px-4 py-3 text-slate-400 max-w-[200px] truncate" title={signup.current_tools || ""}>{signup.current_tools || "-"}</td>
      <td className="px-4 py-3">
        {signup.newsletter ? (
          <span className="text-emerald-400 text-xs font-medium bg-emerald-400/10 px-2 py-0.5 !rounded-[10px]">Yes</span>
        ) : (
          <span className="text-slate-500 text-xs">No</span>
        )}
      </td>
      <td className="px-4 py-3 text-slate-400">{formatDate(signup.created_at)}</td>
      <td className="px-4 py-3 text-right">
        {deleteConfirm === signup.id ? (
          <div className="flex items-center justify-end gap-2">
            <Button
              onClick={() => onConfirmDelete(signup.id)}
              size="sm"
              className="bg-red-500 hover:bg-red-400 text-white !rounded-[10px] h-7 text-xs px-3"
              data-testid={`confirm-delete-${idx}`}
            >
              Confirm
            </Button>
            <Button
              onClick={onCancelDelete}
              size="sm"
              variant="ghost"
              className="text-slate-400 hover:text-white !rounded-[10px] h-7 text-xs px-2"
              data-testid={`cancel-delete-${idx}`}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            onClick={() => onDeleteClick(signup.id)}
            size="sm"
            variant="ghost"
            className="text-slate-500 hover:text-red-400 hover:bg-red-400/10 !rounded-[10px] h-7 w-7 p-0"
            data-testid={`delete-signup-${idx}`}
          >
            <Trash2 size={14} />
          </Button>
        )}
      </td>
    </tr>
  );
}

function TableBody({ loading, paginatedSignups, search, currentPage, deleteConfirm, onDeleteClick, onConfirmDelete, onCancelDelete }) {
  if (loading) {
    return (
      <tr>
        <td colSpan={11} className="text-center text-slate-500 py-12">Loading...</td>
      </tr>
    );
  }

  if (paginatedSignups.length === 0) {
    const message = search ? "No signups match your search." : "No signups yet.";
    return (
      <tr>
        <td colSpan={11} className="text-center text-slate-500 py-12">{message}</td>
      </tr>
    );
  }

  return paginatedSignups.map((s, idx) => (
    <SignupRow
      key={s.id}
      signup={s}
      idx={idx}
      globalIdx={(currentPage - 1) * PAGE_SIZE + idx + 1}
      deleteConfirm={deleteConfirm}
      onDeleteClick={onDeleteClick}
      onConfirmDelete={onConfirmDelete}
      onCancelDelete={onCancelDelete}
    />
  ));
}

function Toolbar({ search, setSearch, onSearch, onAdd, onRefresh, onExport }) {
  return (
    <div className="flex flex-col sm:flex-row gap-4 mb-6">
      <form onSubmit={onSearch} className="flex gap-2 flex-1">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, company, role, country..."
            className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 !rounded-[10px]"
            data-testid="admin-search-input"
          />
        </div>
        <Button
          type="submit"
          className="bg-slate-800 hover:bg-slate-700 text-white !rounded-[10px] gap-2"
          data-testid="admin-search-button"
        >
          <Search size={16} />
          Search
        </Button>
      </form>
      <div className="flex gap-2">
        <Button
          onClick={onAdd}
          className="bg-emerald-500 hover:bg-emerald-400 text-white font-semibold !rounded-[10px] gap-2"
          data-testid="admin-add-signup-button"
        >
          <Plus size={16} />
          Add
        </Button>
        <Button
          onClick={onRefresh}
          variant="ghost"
          className="text-slate-400 hover:text-white hover:bg-slate-800 !rounded-[10px] gap-2"
          data-testid="admin-refresh-button"
        >
          <RefreshCw size={16} />
          Refresh
        </Button>
        <Button
          onClick={onExport}
          className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold !rounded-[10px] gap-2"
          data-testid="admin-export-csv-button"
        >
          <Download size={16} />
          Export CSV
        </Button>
      </div>
    </div>
  );
}

function AddSignupModal({ onClose, onSubmit, loading, error }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    company: "",
    role: "project-manager",
    country: "",
    team_size: "",
    current_tools: "",
    newsletter: false,
  });

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" data-testid="add-signup-modal">
      <div className="bg-slate-900 border border-slate-800 !rounded-[10px] w-full max-w-lg p-6 relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-500 hover:text-white"
          data-testid="close-add-modal"
        >
          <X size={20} />
        </button>

        <h2 className="text-xl font-bold text-white mb-1">Add Signup</h2>
        <p className="text-slate-400 text-sm mb-6">Manually add someone to the beta waitlist.</p>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 !rounded-[10px] p-3 mb-4 text-red-400 text-sm" data-testid="add-signup-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1.5">Full Name *</label>
              <Input
                value={form.full_name}
                onChange={(e) => handleChange("full_name", e.target.value)}
                required
                placeholder="John Doe"
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 !rounded-[10px]"
                data-testid="add-full-name-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1.5">Email *</label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => handleChange("email", e.target.value)}
                required
                placeholder="john@company.com"
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 !rounded-[10px]"
                data-testid="add-email-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1.5">Company</label>
              <Input
                value={form.company}
                onChange={(e) => handleChange("company", e.target.value)}
                placeholder="Company name"
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 !rounded-[10px]"
                data-testid="add-company-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1.5">Role *</label>
              <Select value={form.role} onValueChange={(v) => handleChange("role", v)}>
                <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white !rounded-[10px]" data-testid="add-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {ROLES.map(r => (
                    <SelectItem key={r} value={r} className="text-white hover:bg-slate-700">
                      {r.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1.5">Country</label>
              <Input
                value={form.country}
                onChange={(e) => handleChange("country", e.target.value)}
                placeholder="Sweden"
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 !rounded-[10px]"
                data-testid="add-country-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1.5">Team Size</label>
              <Select value={form.team_size} onValueChange={(v) => handleChange("team_size", v)}>
                <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white !rounded-[10px]" data-testid="add-team-size-select">
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {["1-5", "6-20", "21-50", "51-100", "100+"].map(s => (
                    <SelectItem key={s} value={s} className="text-white hover:bg-slate-700">{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.newsletter}
              onChange={(e) => handleChange("newsletter", e.target.checked)}
              className="rounded border-slate-600 bg-slate-800 text-amber-400"
              id="add-newsletter"
              data-testid="add-newsletter-checkbox"
            />
            <label htmlFor="add-newsletter" className="text-sm text-slate-300">Subscribe to newsletter</label>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              onClick={onClose}
              variant="ghost"
              className="flex-1 text-slate-400 hover:text-white hover:bg-slate-800 !rounded-[10px]"
              data-testid="cancel-add-signup"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold !rounded-[10px]"
              data-testid="submit-add-signup"
            >
              {loading ? "Adding..." : "Add Signup"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Main Component ---

export default function AdminDashboard() {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState("");
  const navigate = useNavigate();

  const {
    signups, loading, totalCount, userName,
    fetchSignups, fetchCount, addSignup, deleteSignup, exportCSV, logout
  } = useAdminData(navigate);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSignups(search);
    setCurrentPage(1);
  };

  const handleAddSignup = async (formData) => {
    setAddLoading(true);
    setAddError("");
    try {
      await addSignup(formData);
      setShowAddForm(false);
      fetchSignups(search);
      fetchCount();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") setAddError(detail);
      else if (Array.isArray(detail)) setAddError(detail.map(e => e.msg || JSON.stringify(e)).join(" "));
      else setAddError("Failed to add signup.");
    } finally {
      setAddLoading(false);
    }
  };

  const handleDeleteSignup = async (signupId) => {
    try {
      await deleteSignup(signupId);
      setDeleteConfirm(null);
      fetchSignups(search);
      fetchCount();
    } catch (_) { /* silently fail — row stays visible */ }
  };

  const handleRefresh = () => {
    setSearch("");
    fetchSignups();
    fetchCount();
  };

  // Pagination
  const totalPages = Math.max(1, Math.ceil(signups.length / PAGE_SIZE));
  const paginatedSignups = signups.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Top bar */}
      <header className="border-b border-slate-800 bg-slate-950/80 sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_project-flow-beta/artifacts/dh5z86or_PM%20Flow.svg"
              alt="PM Flow Logo"
              className="h-8 w-auto"
            />
            <span className="text-slate-500 text-sm hidden sm:inline">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-400 text-sm hidden sm:inline">{userName}</span>
            <Button
              onClick={logout}
              variant="ghost"
              className="text-slate-400 hover:text-red-400 hover:bg-red-400/10 !rounded-[10px] gap-2"
              data-testid="admin-logout-button"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8">
        <StatsCards totalCount={totalCount} signups={signups} />

        <Toolbar
          search={search}
          setSearch={setSearch}
          onSearch={handleSearch}
          onAdd={() => setShowAddForm(true)}
          onRefresh={handleRefresh}
          onExport={exportCSV}
        />

        {/* Table */}
        <div className="bg-slate-900/80 border border-slate-800 !rounded-[10px] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="signups-table">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-800/40">
                  <th className="text-left text-slate-400 font-medium px-4 py-3">#</th>
                  <th className="text-left text-slate-400 font-medium px-4 py-3">Name</th>
                  <th className="text-left text-slate-400 font-medium px-4 py-3">Email</th>
                  <th className="text-left text-slate-400 font-medium px-4 py-3">Company</th>
                  <th className="text-left text-slate-400 font-medium px-4 py-3">Role</th>
                  <th className="text-left text-slate-400 font-medium px-4 py-3">Country</th>
                  <th className="text-left text-slate-400 font-medium px-4 py-3">Team Size</th>
                  <th className="text-left text-slate-400 font-medium px-4 py-3">Current Tools</th>
                  <th className="text-left text-slate-400 font-medium px-4 py-3">Newsletter</th>
                  <th className="text-left text-slate-400 font-medium px-4 py-3">Signed Up</th>
                  <th className="text-right text-slate-400 font-medium px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                <TableBody
                  loading={loading}
                  paginatedSignups={paginatedSignups}
                  search={search}
                  currentPage={currentPage}
                  deleteConfirm={deleteConfirm}
                  onDeleteClick={setDeleteConfirm}
                  onConfirmDelete={handleDeleteSignup}
                  onCancelDelete={() => setDeleteConfirm(null)}
                />
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800">
              <span className="text-sm text-slate-500">
                Page {currentPage} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  variant="ghost"
                  size="sm"
                  className="text-slate-400 hover:text-white !rounded-[10px]"
                  data-testid="pagination-prev"
                >
                  <ChevronLeft size={16} />
                </Button>
                <Button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  variant="ghost"
                  size="sm"
                  className="text-slate-400 hover:text-white !rounded-[10px]"
                  data-testid="pagination-next"
                >
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {showAddForm && (
        <AddSignupModal
          onClose={() => { setShowAddForm(false); setAddError(""); }}
          onSubmit={handleAddSignup}
          loading={addLoading}
          error={addError}
        />
      )}
    </div>
  );
}
